import { usePermissions } from "@/hooks/usePermissions"
import { api } from "@/services/api"
import { useAuthStore } from "@/stores/auth"
import type { HouseholdResponse } from "@/types/household"
import { unwrapStandardResponse } from "@/utils/apiResponse"
/**
 * Hook to get the current user's own household
 * Uses the /households/me endpoint which filters by user_id
 */
import { useQuery } from "@tanstack/react-query"

export function useMyHousehold() {
  const { hasPermission } = usePermissions()
  const accessToken = useAuthStore((state) => state.accessToken)

  const hasRequiredPermission =
    hasPermission("households:read") || hasPermission("households:read_own")

  return useQuery({
    queryKey: ["households", "me"],
    queryFn: async (): Promise<HouseholdResponse | null> => {
      const response =
        await api.households.readHouseholdMeApiV1HouseholdsMeGet()
      const data = unwrapStandardResponse<HouseholdResponse>(
        response,
      ) as HouseholdResponse
      return data ?? null
    },
    // Only fire when we have both a valid token AND the right permissions.
    // Without the token check, stale scopes in localStorage can cause
    // an infinite 401 retry loop (the query fires, gets 401, the global
    // error handler clears the cache, the query refires immediately).
    enabled: !!accessToken && hasRequiredPermission,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: false,
  })
}
